/**
 * Address utility functions for validation and comparison
 */

import { StandardizedAddress } from '@/hooks/useAddressValidation';

interface AddressInput {
  street: string;
  city: string;
  state: string;
  zipcode: string;
}

/**
 * Normalizes a string for comparison by trimming, lowercasing, 
 * and removing common abbreviation differences
 */
function normalizeForComparison(str: string): string {
  return str
    .toLowerCase()
    .trim()
    // Normalize common street abbreviations
    .replace(/\bstreet\b/g, 'st')
    .replace(/\bavenue\b/g, 'ave')
    .replace(/\bboulevard\b/g, 'blvd')
    .replace(/\bdrive\b/g, 'dr')
    .replace(/\broad\b/g, 'rd')
    .replace(/\blane\b/g, 'ln')
    .replace(/\bcourt\b/g, 'ct')
    .replace(/\bplace\b/g, 'pl')
    .replace(/\bsuite\b/g, 'ste')
    .replace(/\bapartment\b/g, 'apt')
    .replace(/\bnorth\b/g, 'n')
    .replace(/\bsouth\b/g, 's')
    .replace(/\beast\b/g, 'e')
    .replace(/\bwest\b/g, 'w')
    // Remove extra spaces
    .replace(/\s+/g, ' ');
}

/**
 * Checks if the standardized address differs meaningfully from the user's input.
 * Returns true if the dialog should be shown to let the user choose.
 */
export function addressDiffersFromStandardized(
  input: AddressInput,
  standardized?: StandardizedAddress | null
): boolean {
  if (!standardized) return false;

  const normalizedInputStreet = normalizeForComparison(input.street);
  const normalizedStandardizedStreet = normalizeForComparison(standardized.street);
  
  const normalizedInputCity = normalizeForComparison(input.city);
  const normalizedStandardizedCity = normalizeForComparison(standardized.city);
  
  const normalizedInputState = normalizeForComparison(input.state);
  const normalizedStandardizedState = normalizeForComparison(standardized.state);
  
  // Compare zip codes without the +4 extension
  const inputZip = input.zipcode.split('-')[0].trim();
  const standardizedZip = standardized.zipcode.split('-')[0].trim();

  // Check if any field differs
  const streetDiffers = normalizedInputStreet !== normalizedStandardizedStreet;
  const cityDiffers = normalizedInputCity !== normalizedStandardizedCity;
  const stateDiffers = normalizedInputState !== normalizedStandardizedState;
  const zipDiffers = inputZip !== standardizedZip;

  return streetDiffers || cityDiffers || stateDiffers || zipDiffers;
}

/**
 * Formats an address object into a display-friendly array of lines
 */
export function formatAddressLines(
  address: { street: string; street2?: string; city: string; state: string; zipcode: string; plus4?: string }
): string[] {
  const lines: string[] = [];
  lines.push(address.street);
  
  if (address.street2) {
    lines.push(address.street2);
  }
  
  const zip = address.plus4 ? `${address.zipcode}-${address.plus4}` : address.zipcode;
  lines.push(`${address.city}, ${address.state} ${zip}`);
  
  return lines;
}
